import React, { useState, useEffect, useRef } from 'react';
import { Search, Upload, X, ImageOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { api } from '../lib/api';
import DropZone from './DropZone';
import { toast } from 'sonner';

/**
 * Selector visual de la Biblioteca Multimedia corporativa (experiencia tipo WordPress).
 * El usuario nunca ve URLs: selecciona, sube, guarda.
 */
export default function MediaPicker({ open, onClose, onSelect, ownerId = 'matriz', type = 'image' }) {
  const [files, setFiles] = useState([]);
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [folder, setFolder] = useState('');
  const [folders, setFolders] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.getMedia({ q, type, folder }).then(setFiles).catch(() => {});

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  useEffect(() => {
    if (open) { load(); api.getMediaFolders().then(setFolders).catch(() => {}); }
    // eslint-disable-next-line
  }, [open, q, folder]);

  const handleFiles = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      for (const file of fileList) {
        const doc = await api.uploadMedia(file, ownerId, folder);
        toast.success(`${doc.fileName} subido a la biblioteca`);
      }
      setQ(''); setQInput('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir el archivo');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const activeFiles = files.filter((f) => f.active);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="media-picker">
        <DialogHeader>
          <DialogTitle>Biblioteca Multimedia</DialogTitle>
          <DialogDescription className="sr-only">Selecciona un archivo de la biblioteca corporativa o sube uno nuevo.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Buscar archivo…"
              className="w-full border border-stone-300 rounded pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              data-testid="media-picker-search"
            />
          </div>
          <select
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            className="border border-stone-300 rounded px-2.5 py-2 text-sm bg-white max-w-44 focus:outline-none focus:ring-2 focus:ring-amber-400"
            data-testid="media-picker-folder"
          >
            <option value="">Todas las carpetas</option>
            {folders.map((f) => <option key={f.id} value={f.path}>{f.path}</option>)}
          </select>
          <label className="btn-natur flex items-center gap-2 px-4 py-2 rounded font-bold text-sm cursor-pointer">
            <Upload className="w-4 h-4" /> {uploading ? 'Subiendo…' : 'Subir nuevo archivo'}
            <input ref={inputRef} type="file" multiple accept={`${type}/*`} className="hidden" onChange={(e) => handleFiles(Array.from(e.target.files || []))} disabled={uploading} data-testid="media-picker-upload" />
          </label>
        </div>

        <DropZone onFiles={handleFiles} uploading={uploading} accept={`${type}/*`} compact />

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 max-h-96 overflow-y-auto pt-2 content-start" data-testid="media-picker-grid">
          {activeFiles.map((f) => (
            <button
              key={f.id}
              onClick={() => { onSelect(api.mediaUrl(f.id)); onClose(); }}
              className="group relative border border-stone-200 rounded-lg overflow-hidden hover:ring-2 hover:ring-amber-400 transition-shadow bg-stone-50 h-[120px] flex flex-col"
              title={f.fileName}
              data-testid={`media-item-${f.id}`}
            >
              {type === 'video' ? (
                <video src={api.mediaUrl(f.id)} className="w-full flex-1 min-h-0 object-cover bg-black" preload="metadata" muted />
              ) : (
                <img src={api.mediaThumb(f.id, 240)} alt={f.fileName} className="w-full flex-1 min-h-0 object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
              )}
              <span className="block w-full shrink-0 text-[10px] text-stone-500 truncate px-1.5 py-1 bg-white">{f.fileName}</span>
            </button>
          ))}
          {activeFiles.length === 0 && (
            <div className="col-span-full text-center py-12 text-stone-400">
              <ImageOff className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">No hay archivos. Sube el primero.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
